/** Activity feed for a job — escalation comments, view-only translations,
 *  AND reviewer notes from "Edit before approve" on every stage. All
 *  three are merged chronologically so the reviewer sees the full
 *  trail of human commentary on the job.
 *
 *  Backed by:
 *   GET /api/v1/jobs/{job_id}/escalations
 *   GET /api/v1/jobs/{job_id}/translations
 *   GET /api/v1/jobs/{job_id}/agents             (filtered to rows with edits_json)
 */
import { useCallback, useEffect, useState } from 'react';
import { MessageSquare, RefreshCw, AlertCircle, Loader2, Languages, PenLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';
import { fmtDateTime } from '@/lib/datetime';
import type { EscalationTask, EscalationStatus } from '@/pages/escalations/types';
import type { AgentResult } from '@/types';

interface Props {
  jobId: string;
  /** Bump this when the parent thinks new activity may have been added
   *  (escalate, translate, approve-with-notes …). Triggers a refetch. */
  refreshKey?: number;
}

interface TranslationEvent {
  id: string;
  created_at: string;
  actor_id: string | null;
  target_language: string | null;
  source_language: string | null;
  input_chars: number | null;
  output_chars: number | null;
}

interface ReviewerNote {
  id: string;
  agent_name: string;
  at: string;
  approved_by: string | null;
  /** Free-form note text (typically from output.reviewer_notes or the
   *  whole edits_json patch when no explicit notes field). */
  text: string;
  edits: Record<string, unknown>;
}

type FeedItem =
  | { kind: 'comment'; at: string; data: EscalationTask }
  | { kind: 'translation'; at: string; data: TranslationEvent }
  | { kind: 'reviewer_note'; at: string; data: ReviewerNote };

const STATUS_CLS: Record<EscalationStatus, string> = {
  open: 'bg-amber-50 text-amber-800 border-amber-200',
  acknowledged: 'bg-blue-50 text-blue-700 border-blue-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
};

function extractNote(edits: Record<string, unknown>): string {
  // Common shapes the StageReviewPanel "Edit before approve" produces.
  const candidates = ['reviewer_notes', 'reviewer_note', 'note', 'comment', 'notes'];
  for (const k of candidates) {
    const v = edits[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  // Fallback — dump the patch as JSON so nothing is lost.
  try {
    return JSON.stringify(edits, null, 2);
  } catch {
    return '[unparseable reviewer note]';
  }
}

function reviewerNotesFromAgents(agents: AgentResult[]): ReviewerNote[] {
  return agents
    .filter((a) => a.edits_json && Object.keys(a.edits_json).length > 0)
    .map<ReviewerNote>((a) => ({
      id: a.id,
      agent_name: a.agent_name,
      at: a.approved_at ?? a.completed_at ?? a.started_at ?? a.created_at,
      approved_by: a.approved_by ?? null,
      text: extractNote(a.edits_json ?? {}),
      edits: a.edits_json ?? {},
    }));
}

function merge(
  comments: EscalationTask[],
  translations: TranslationEvent[],
  notes: ReviewerNote[],
): FeedItem[] {
  const items: FeedItem[] = [
    ...comments.map<FeedItem>((c) => ({ kind: 'comment', at: c.created_at, data: c })),
    ...translations.map<FeedItem>((t) => ({ kind: 'translation', at: t.created_at, data: t })),
    ...notes.map<FeedItem>((n) => ({ kind: 'reviewer_note', at: n.at, data: n })),
  ];
  items.sort((a, b) => a.at.localeCompare(b.at));
  return items;
}

function CommentRow({ c }: { c: EscalationTask }) {
  return (
    <li className="rounded border bg-muted/20 px-3 py-2">
      <div className="flex items-start gap-2 flex-wrap mb-1">
        <Badge variant="outline" className="text-[10px]">
          <MessageSquare className="h-2.5 w-2.5 mr-1" aria-hidden="true" />
          Comment
        </Badge>
        <Badge variant="outline" className={`text-[10px] ${STATUS_CLS[c.status]}`}>
          {c.status}
        </Badge>
        <span className="text-[11px] text-muted-foreground">
          Stage: <span className="font-medium text-foreground">{c.stage}</span>
        </span>
        <span className="text-[11px] font-mono text-muted-foreground ml-auto">
          {fmtDateTime(c.created_at)}
        </span>
      </div>
      <p className="text-sm whitespace-pre-wrap">{c.comment}</p>
      {c.resolution_note && (
        <p className="mt-1 text-xs italic text-muted-foreground border-l-2 pl-2">
          Resolution: {c.resolution_note}
        </p>
      )}
    </li>
  );
}

function TranslationRow({ t }: { t: TranslationEvent }) {
  const src = t.source_language ?? 'auto';
  const tgt = t.target_language ?? '?';
  return (
    <li className="rounded border bg-muted/20 px-3 py-2">
      <div className="flex items-start gap-2 flex-wrap mb-1">
        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
          <Languages className="h-2.5 w-2.5 mr-1" aria-hidden="true" />
          Translated
        </Badge>
        <span className="text-[11px] text-muted-foreground">
          {src} → <span className="font-medium text-foreground">{tgt}</span>
        </span>
        {t.input_chars != null && (
          <span className="text-[11px] text-muted-foreground">· {t.input_chars} chars in</span>
        )}
        <span className="text-[11px] font-mono text-muted-foreground ml-auto">
          {fmtDateTime(t.created_at)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground italic">
        View-only translation — the pipeline always reads the original text.
      </p>
    </li>
  );
}

function ReviewerNoteRow({ n }: { n: ReviewerNote }) {
  return (
    <li className="rounded border bg-muted/20 px-3 py-2">
      <div className="flex items-start gap-2 flex-wrap mb-1">
        <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
          <PenLine className="h-2.5 w-2.5 mr-1" aria-hidden="true" />
          Reviewer note
        </Badge>
        <span className="text-[11px] text-muted-foreground">
          Stage: <span className="font-medium text-foreground">{n.agent_name}</span>
        </span>
        <span className="text-[11px] font-mono text-muted-foreground ml-auto">
          {fmtDateTime(n.at)}
        </span>
      </div>
      <p className="text-sm whitespace-pre-wrap">{n.text}</p>
    </li>
  );
}

export function JobCommentsPanel({ jobId, refreshKey }: Props) {
  const [comments, setComments] = useState<EscalationTask[]>([]);
  const [translations, setTranslations] = useState<TranslationEvent[]>([]);
  const [reviewerNotes, setReviewerNotes] = useState<ReviewerNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api
        .get<{ data: EscalationTask[] }>(`/api/v1/jobs/${jobId}/escalations`)
        .catch(() => ({ data: [] as EscalationTask[] })),
      api
        .get<{ data: TranslationEvent[] }>(`/api/v1/jobs/${jobId}/translations`)
        .catch(() => ({ data: [] as TranslationEvent[] })),
      api
        .get<AgentResult[]>(`/api/v1/jobs/${jobId}/agents`)
        .catch(() => [] as AgentResult[]),
    ])
      .then(([c, t, agents]) => {
        setComments(c.data);
        setTranslations(t.data);
        setReviewerNotes(reviewerNotesFromAgents(agents));
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load activity.');
      })
      .finally(() => { setLoading(false); });
  }, [jobId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const feed = merge(comments, translations, reviewerNotes);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Activity &amp; comments ({feed.length})
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          aria-label="Refresh activity"
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && feed.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Loading activity…
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription className="flex items-center gap-3 text-xs">
              {error}
              <Button variant="ghost" size="sm" onClick={load}>Retry</Button>
            </AlertDescription>
          </Alert>
        ) : feed.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1">
            No activity yet. Notes from <em>Edit before approve</em> on each stage,
            escalation comments, and view-only translations all land here in
            chronological order.
          </p>
        ) : (
          <ul className="space-y-3">
            {feed.map((it) =>
              it.kind === 'comment' ? (
                <CommentRow key={`c-${it.data.id}`} c={it.data} />
              ) : it.kind === 'translation' ? (
                <TranslationRow key={`t-${it.data.id}`} t={it.data} />
              ) : (
                <ReviewerNoteRow key={`n-${it.data.id}`} n={it.data} />
              ),
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
